/**
 * Unified error reporting API.
 * Auto-detects server vs client and routes to reportServerError or reportClientError.
 * All errors are logged to console and sent to Sentry with full metadata.
 */

import { reportClientError } from "@/lib/observability/report-client-error";
import { reportServerError } from "@/lib/observability/report-server-error";
import type {
  ApiRouteMeta,
  ErrorReportContext,
} from "@/lib/observability/types";

export type ReportErrorContext = Omit<ErrorReportContext, "source"> & {
  source?: string;
};

export type ReportErrorOptions = ReportErrorContext & {
  step?: string;
  inputSummary?: string;
  cause?: string;
};

/**
 * Report a critical error. Use when:
 * - The operation failed with NO fallback or recovery
 * - The user's intended action cannot complete
 * - DB failure, unrecoverable crash, service fully unavailable
 *
 * Maps to Sentry severity "error" (default).
 */
export function reportError(
  error: unknown,
  context: ReportErrorContext
): string | undefined {
  const fullContext: ErrorReportContext = {
    handled: context.handled ?? true,
    ...context,
    source: context.source ?? "unknown",
  };

  if (typeof window === "undefined") {
    return reportServerError(error, fullContext);
  }
  return reportClientError(error, fullContext);
}

/**
 * Factory for consistent error reporting with a fixed source and optional feature.
 * Use in modules where the source is known (e.g. API route, component, service).
 *
 * @example
 * const report = createErrorReporter("api.user.me", "user");
 * report(error, { request, extra: { step: "getAuthenticatedUser" } });
 */
export function createErrorReporter(
  source: string,
  feature?: string
): (
  error: unknown,
  options?: Omit<ReportErrorContext, "source"> & { feature?: string }
) => string | undefined {
  return (error, options = {}) => {
    const { feature: optFeature, ...rest } = options;
    return reportError(error, {
      source,
      feature: optFeature ?? feature,
      ...rest,
    });
  };
}

/**
 * Builds ErrorReportContext for API route handlers.
 * Use when you have the request and route meta.
 */
export function getApiRouteContext(
  request: Request | undefined,
  meta: ApiRouteMeta
): Pick<ErrorReportContext, "source" | "route" | "method" | "request"> {
  return {
    source: meta.source ?? `api.${meta.route}`,
    route: meta.route,
    method: meta.method,
    request: request ?? undefined,
  };
}

/**
 * Report a non-critical issue. Use when:
 * - The operation failed but a fallback/recovery succeeded
 * - The user's intended action still completes
 * - Cache miss, optional token refresh, feature degradation
 *
 * Sent to Sentry as **level error** (errors-only `beforeSend`) with tags
 * `original_severity=warning` and `failure_classification=degraded`.
 */
export function reportWarning(
  error: unknown,
  context: ReportErrorContext
): string | undefined {
  return reportError(error, { ...context, severity: "warning" });
}

/**
 * Report an informational event. Use for:
 * - Cache invalidation, optional data unavailable
 * - Non-actionable but useful for debugging
 *
 * Breadcrumb-only in `reportServerError` / `reportClientError` — does not
 * create a Sentry issue (by design).
 */
export function reportInfo(
  error: unknown,
  context: ReportErrorContext
): string | undefined {
  return reportError(error, { ...context, severity: "info" });
}

/**
 * Explicitly suppress reporting for a handled, expected condition.
 * This keeps intent visible to humans and static audits while avoiding
 * a full Sentry event for known-noisy paths.
 */
export function suppressErrorReporting(
  reason: string,
  context: ReportErrorContext
): string | undefined {
  return reportInfo(new Error(reason), {
    ...context,
    handled: true,
    expected: true,
    suppressionReason: reason,
    extra: {
      ...context.extra,
      suppressionReason: reason,
    },
  });
}

/**
 * Helper to report an error with detailed extra context.
 * Use for catch blocks where you want step, cause, and input summary.
 */
export function reportErrorWithContext(
  error: unknown,
  context: ReportErrorContext & {
    step?: string;
    cause?: string;
    inputSummary?: string;
  }
): string | undefined {
  const extra = {
    ...context.extra,
    ...(context.step && { step: context.step }),
    ...(context.cause && { cause: context.cause }),
    ...(context.inputSummary && { inputSummary: context.inputSummary }),
    errorMessage: error instanceof Error ? error.message : String(error),
  };

  return reportError(error, {
    ...context,
    extra,
  });
}
